import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import { useIsScrolledToBottom } from "@via/hooks/useIsScrolledToBottom.tsx";
import {
  useAgreeUserAgreement,
  useGetMandatoryUserAgreementsHandler,
} from "@via/pages/authentication/self-service-signup/authentication.api.tsx";
import React from "react";
import { classNames } from "@via/utils/helpers.tsx";
import LoginLink from "@via/components/login-link.tsx";

interface StepProps {
  onNextStep: () => void;
  isUpgrade?: boolean;
}

const UserAgreementForm: React.FC<StepProps> = ({ onNextStep, isUpgrade }) => {
  const isBottom = useIsScrolledToBottom("via-view-wrapper");

  const {
    data: mandatoryUserAgreements,
    isPending: isGetMandatoryUserAgreementsLoading,
    isSuccess: isGetMandatoryUserAgreementsSuccess,
    isError: isGetMandatoryUserAgreementsError,
    refetch: refetchMandatoryUserAgreements,
    isRefetching: isMandatoryUserAgreementsRefetching,
  } = useGetMandatoryUserAgreementsHandler();

  // The progress flows drop this step when the list is empty (#989). The Accept button is only
  // offered when there is agreement text to accept, never while loading or after a failure.
  const hasAgreementsToAccept =
    isGetMandatoryUserAgreementsSuccess &&
    (mandatoryUserAgreements || []).length > 0;

  const {
    mutateAsync: agreeUserAgreement,
    isPending: isAgreeUserAgreementLoading,
  } = useAgreeUserAgreement();

  const handleAgreement = async () => {
    /**
     * For now, display all non-accepted user agreements on one page and let the user agree with one click
     */
    await Promise.all(
      (mandatoryUserAgreements || []).map(agreement =>
        agreeUserAgreement({ id: agreement?.id })
      )
    );

    onNextStep();
  };

  const isAgreeDisabled = isUpgrade
    ? false
    : isAgreeUserAgreementLoading || !isBottom;

  return (
    <div className={classNames("px-8 pt-1 pb-24", isUpgrade ? "" : "md:w-5xl")}>
      {isGetMandatoryUserAgreementsLoading ? (
        <div className="mt-6 flex flex-col gap-4">
          <SkeletonLoader count={6} />
        </div>
      ) : isGetMandatoryUserAgreementsSuccess ? (
        (mandatoryUserAgreements || []).map(agreement => (
          <div key={agreement.id} className="my-2">
            <div
              className="max-w-none"
              dangerouslySetInnerHTML={{ __html: agreement.content }}
            />
            <p className="text-sm text-gray-500 mt-2">
              Language: {agreement.language_code} | Version: {agreement.version}
            </p>
          </div>
        ))
      ) : isGetMandatoryUserAgreementsError ? (
        <div className="mt-6 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-gray-700">
            The user agreement could not be loaded. Try again, and contact
            support if the problem persists.
          </p>
          <button
            onClick={() => refetchMandatoryUserAgreements()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
            disabled={isMandatoryUserAgreementsRefetching}>
            <Spinner loading={isMandatoryUserAgreementsRefetching}>
              Try again
            </Spinner>
          </button>
        </div>
      ) : null}

      <div className="fixed bottom-0 left-0 w-full z-50">
        {hasAgreementsToAccept ? (
          <div className="max-w-screen-md mx-auto flex justify-center gap-5 px-4 py-5">
            <button
              onClick={handleAgreement}
              className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
              disabled={isAgreeDisabled}>
              <Spinner loading={isAgreeUserAgreementLoading}>
                Accept End User License Agreement
              </Spinner>
            </button>
          </div>
        ) : null}
        <LoginLink className="-mt-2 mb-4" logoutBeforeLogin={true} />
      </div>
    </div>
  );
};

export default UserAgreementForm;
